// Port of companion/src/rules.mjs at tag js-core-freeze-2026-09-05 (HEAD cb2d250): the eight rules
// (R1-R8) and the character coach. The same compile and script give the same findings in the same
// order with the same messages, and the same coached characters with the same counts. The only
// shape changes are the ones the editor-client lint profile forces (the frozen `for...of` and
// `continue` are forEach and `return`, `.at(-1)` is a last-index read); none changes an output.
// The frozen comments' own words (rules.mjs:131, :144), for the spell check:
// cspell:ignore unscored unrouted
import type {
  Analysis,
  Block,
  Branch,
  CharacterBlock,
  CharacterCoach,
  CoachCheck,
  Marker,
  MarkerReason,
  PriceLadder,
  RuleCode,
  RuleFinding,
  RulesInput,
  Scene,
  ScriptAst,
} from '../../dorian-shared/types/episodeScript';
import { structureReport } from './script/compile';
import { parse } from './script/parse';

export const PRICE_LADDER: PriceLadder = Object.freeze([5, 10, 25, 150] as const);

const SEVERITY = Object.freeze({
  R1: 'error',
  R2: 'warning',
  R3: 'error',
  R4: 'error',
  R5: 'error',
  R6: 'warning',
  R7: 'warning',
  R8: 'warning',
} as const);

// The four checks the character coach runs, in the order it shows them.
export const COACH_CHECKS = Object.freeze([
  'Introduced clearly',
  'Takes part in a choice the reader makes',
  'Reacts after that choice',
  'Comes back when an earlier story memory is used',
] as const);

/** A word for some of the marker reasons; the rest fall through (rules.mjs:21-26, :36-44). */
type ReasonWords = Readonly<Partial<Record<MarkerReason, string>>>;

// R5 names what the compiler could not resolve. "mood" is the word the writer uses; "expression"
// is the engine's.
const REFERENCE_WORDS: ReasonWords = Object.freeze({
  'unknown character': 'character',
  'unknown expression': 'mood',
  'unknown location': 'location',
  'unknown target': 'scene',
});

// A scene target is resolved inside the script itself, so it is unresolved in every mode. A
// character, mood or location is resolved against the book, and a fresh compile has no book behind
// it: a name it cannot look up is a name the owner has yet to create, exactly as compileFresh
// already reads an unknown variable. Only a mapped compile can say a name is genuinely wrong.
const SCRIPT_LOCAL_REASONS = new Set<string>(['unknown target']);

// R7 repeats the compiler's own diagnostic rather than "a marker was left".
const DIAGNOSTIC_WORDS: ReasonWords = Object.freeze({
  'unknown character': 'unknown character',
  'unknown expression': 'unknown mood',
  'unknown location': 'unknown location',
  'unknown target': 'unknown scene',
  'unknown variable': 'unknown variable',
  'new variable': "a variable the book doesn't have yet",
  'content before first scene heading': "something it couldn't read above the first scene",
  // Round 1, lane A (docs/briefs/ROUND1-SPEC-2026-09-05.md section J): the four reasons Round 1 adds.
  'unknown check operator': 'an unknown check word',
  'read-only label': "a label the script can't change",
  'unresolved library item': "a library item the script can't name yet",
  'malformed value': "a value the episode can't take",
});

function finding(rule: RuleCode, sceneName: string | null, line: number | null, message: string, suggestion: string): RuleFinding {
  return {
    rule, severity: SEVERITY[rule], sceneName, line, message, suggestion,
  };
}

// BAR-QA-08: R2 used to test `price > 0`, so a negative price was silently waved through. A price
// is free (0) or a rung of the season ladder; anything else — negative, fractional, or simply off
// the ladder — is a finding.
function priceIsAllowed(price: number): boolean {
  return Number.isInteger(price) && price >= 0 && (price === 0 || (PRICE_LADDER as readonly number[]).includes(price));
}

function priceFinding(sceneName: string | null, line: number, raw: number | null | undefined): RuleFinding | null {
  const price = Number(raw ?? 0);
  if (priceIsAllowed(price)) return null;
  if (!Number.isFinite(price) || !Number.isInteger(price) || price < 0) {
    return finding(
      'R2',
      sceneName,
      line,
      `A price has to be a whole number of hearts, zero or more, so ${raw} won't work.`,
      "Write 0 (or no price at all) for a free option, or one of the season's prices: 5, 10, 25, 150.",
    );
  }
  return finding(
    'R2',
    sceneName,
    line,
    `The price ${raw} isn't one of the season's prices: 5, 10, 25 or 150.`,
    'Change it to the nearest of those.',
  );
}

function linesOf(script: string): string[] {
  return String(script ?? '').replace(/\r\n?/g, '\n').split('\n');
}

function sceneForLine(ast: ScriptAst, line: number): string | null {
  let current: string | null = ast.scenes[0]?.name ?? null;
  ast.scenes.some((scene) => {
    if (scene.line > line) return true;
    current = scene.name;
    return false;
  });
  return current;
}

/** The nodes the compiler made of one scene the writer wrote (rules.mjs:87-95). */
interface SceneGroup {
  name: string;
  nodes: Branch[];
}

// The compiler splits a long scene into several nodes at its choices, so the graph carries names
// the writer never wrote ("SCENE (continuation 2)"). A finding always speaks about the scene the
// writer sees: the scene cannot be reached when its first node cannot, and it stops when its last
// node stops.
// Round 1, lane A (spec F.2 and H.2's "analysis unchanged"): a node now carries the writer's heading
// as its `description` and a slug as its `title` (the engine refuses anything else), so the name a
// finding speaks with is the description when there is one — otherwise every multi-word heading
// would come back to the writer as `waking_in_bedroom` with no line, because the heading lookup
// below (`lines`) is by the writer's own words. ep02, whose descriptions are empty and whose titles
// are already slugs, is unchanged by this.
function scenesOf(nodes: Branch[]): SceneGroup[] {
  const groups: SceneGroup[] = [];
  nodes.forEach((node) => {
    const written = node.description !== undefined && node.description !== null && node.description !== ''
      ? node.description
      : node.title;
    const name = String(written ?? `Node ${node.id}`).replace(/ \(continuation \d+\)$/, '');
    const last: SceneGroup | undefined = groups[groups.length - 1];
    if (last?.name === name) last.nodes.push(node);
    else groups.push({ name, nodes: [node] });
  });
  return groups;
}

function normalizeName(value: unknown): string {
  return String(value ?? '').toLocaleLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

// A character is a whole name, not a bag of words: MR WHITE and MRS WHITE are two people. The full
// normalised name has to appear as a run of words.
function containsName(text: string, name: string): boolean {
  const needle = normalizeName(name).split('_').filter(Boolean);
  if (!needle.length) return false;
  const words = normalizeName(text).split('_').filter(Boolean);
  return words.some((_, at) => needle.every((word, offset) => words[at + offset] === word));
}

function sceneText(lines: string[], ast: ScriptAst, index: number): string {
  const start = Math.max(0, ast.scenes[index].line - 1);
  const end = index + 1 < ast.scenes.length ? ast.scenes[index + 1].line - 1 : lines.length;
  return lines.slice(start, end).join('\n');
}

// The placeholder scene name a malformed opening is filed under is the parser's bookkeeping, not a
// name the writer wrote, so that diagnostic quotes nothing.
const UNNAMED_REASONS = new Set<string>(['content before first scene heading']);

function diagnostic(marker: Marker): string {
  const word = DIAGNOSTIC_WORDS[marker.reason] ?? marker.reason;
  const value = String(marker.value ?? '').trim();
  return value && !UNNAMED_REASONS.has(marker.reason) ? `${word} “${value}”` : word;
}

function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

const isCharacter = (block: Block): block is CharacterBlock => block.type === 'character';

// BAR's four checks, unscored. The count is the whole answer ("3 of 4"); a percentage would claim
// a precision none of these four have.
function characterCoach(ast: ScriptAst, lines: string[]): CharacterCoach[] {
  const names = [...new Set(ast.scenes.flatMap((scene) => scene.blocks.filter(isCharacter).map((block) => block.name)))];
  const texts = ast.scenes.map((_, index) => sceneText(lines, ast, index));

  const speaks = (scene: Scene, name: string): boolean => scene.blocks.some((block) => block.type === 'character' && block.name === name);
  const narrated = (scene: Scene, name: string): boolean => scene.blocks.some((block) =>
    (block.type === 'narration' || block.type === 'choice') && containsName(block.text, name));
  const appears = (index: number, name: string): boolean => speaks(ast.scenes[index], name) || containsName(texts[index], name);

  // What a choice can reach: the scene an answer names, and the scene after the one holding the
  // choice, which is where an unrouted answer falls through to.
  const afterChoice = new Set<string>();
  ast.scenes.forEach((scene, index) => {
    if (!scene.blocks.some((block) => block.type === 'choice')) return;
    scene.blocks.forEach((block) => {
      if (block.type !== 'choice') return;
      block.answers.forEach((answer) => {
        if (answer.targetScene) afterChoice.add(answer.targetScene.toLocaleLowerCase());
      });
    });
    const next: Scene | undefined = ast.scenes[index + 1];
    if (next) afterChoice.add(next.name.toLocaleLowerCase());
  });
  // The compiler splits a scene at its choice, so a line written below the choice already sits in
  // the node the answers continue into.
  const reactsBelowChoice = (scene: Scene, name: string): boolean => {
    const at = scene.blocks.findIndex((block) => block.type === 'choice');
    return at >= 0 && scene.blocks.slice(at + 1).some((block) => block.type === 'character' && block.name === name);
  };

  const setScenes = new Map<string, number[]>();
  const useScenes = new Map<string, number[]>();
  ast.scenes.forEach((scene, index) => {
    scene.blocks.forEach((block) => {
      if (block.type !== 'directive') return;
      const key = normalizeName('variable' in block ? block.variable : undefined);
      if (!key) return;
      if (block.directive === 'remember' || block.directive === 'forget') push(setScenes, key, index);
      if (block.directive === 'remember' || block.directive === 'forget' || block.directive === 'if') push(useScenes, key, index);
    });
  });

  return names.map((name): CharacterCoach => {
    const first = ast.scenes.findIndex((_, index) => appears(index, name));
    const introduced = first >= 0 && (speaks(ast.scenes[first], name) || narrated(ast.scenes[first], name));
    const inChoice = ast.scenes.some((scene, index) =>
      appears(index, name) && scene.blocks.some((block) => block.type === 'choice'));
    const reacts = ast.scenes.some((scene) =>
      (afterChoice.has(scene.name.toLocaleLowerCase()) && speaks(scene, name)) || reactsBelowChoice(scene, name));
    // Changed context: a variable set in a scene this character was in, and the character present
    // again where that variable is remembered or checked.
    const returns = [...setScenes].some(([key, sets]) => sets.some((set) =>
      appears(set, name) && (useScenes.get(key) ?? []).some((use) => use > set && appears(use, name))));

    const results = [introduced, inChoice, reacts, returns];
    return {
      name,
      met: results.filter(Boolean).length,
      checks: COACH_CHECKS.map((label, index): CoachCheck => ({ label, met: results[index] })),
    };
  });
}

// The boundary is as forgiving as the frozen one (`compiled?.`, `script ?? ''`): the signature is
// wider than AnalyseFn for that reason and assignable to it.
export function analyse(compiled: RulesInput | null | undefined, script: string | null | undefined): Analysis {
  const source = String(script ?? '');
  const lines = linesOf(source);
  const ast = parse(source);
  const findings: RuleFinding[] = [];

  for (let sceneIndex = 0; sceneIndex < ast.scenes.length; sceneIndex += 1) {
    const scene = ast.scenes[sceneIndex];
    for (let blockIndex = 0; blockIndex < scene.blocks.length; blockIndex += 1) {
      const block = scene.blocks[blockIndex];
      if (block.type === 'choice') {
        const hasFallThrough = blockIndex < scene.blocks.length - 1 || sceneIndex < ast.scenes.length - 1;
        block.answers.forEach((answer) => {
          if (!answer.target && !hasFallThrough) {
            findings.push(finding(
              'R1',
              scene.name,
              answer.line,
              `“${answer.text}” has nowhere to go, and there's no scene after it to catch the reader.`,
              'Give this answer a destination with an arrow and scene name.',
            ));
          }
          const answerPriceFinding = priceFinding(scene.name, answer.line, answer.price);
          if (answerPriceFinding) findings.push(answerPriceFinding);
          if (answer.text.length > 40) {
            findings.push(finding(
              'R8',
              scene.name,
              answer.line,
              `This answer is ${answer.text.length} characters long. Answers fit up to 40.`,
              'Shorten the answer to 40 characters or fewer.',
            ));
          }
        });
        // The prompt is the last line before the answers, per the format spec: the paragraph above
        // may be several lines and only its last one is the prompt the engine carries on the
        // choice step.
        const promptLines = String(block.text ?? '').split('\n');
        const prompt = promptLines[promptLines.length - 1] ?? '';
        if (prompt.length > 40) {
          findings.push(finding(
            'R8',
            scene.name,
            block.line + promptLines.length - 1,
            `This choice prompt is ${prompt.length} characters long. Prompts fit up to 40.`,
            'Shorten the prompt to 40 characters or fewer.',
          ));
        }
        if (block.answers.length > 0 && block.answers.every((answer) => Number(answer.price) > 0)) {
          findings.push(finding(
            'R6',
            scene.name,
            block.line,
            'Every answer in this choice is priced, so there is no free way forward.',
            'Make at least one answer free.',
          ));
        }
      }
      if (block.type === 'hub') {
        block.options.forEach((option) => {
          const optionPriceFinding = priceFinding(scene.name, option.line, option.price);
          if (optionPriceFinding) findings.push(optionPriceFinding);
        });
      }
    }
  }

  // The compiler's markers, indexed by the line the writer sees them on. They feed both R5 (the
  // reference itself) and R7 (what the compile said here).
  const markers = compiled?.markers ?? [];
  const markersByLine = new Map<number, Marker[]>();
  markers.forEach((marker) => {
    push(markersByLine, Number(marker.line), marker);
  });

  // R5 — an unresolved reference, one finding per occurrence, carrying the name the writer typed.
  // SCRIPT_LOCAL_REASONS above says why a fresh compile reports only scene targets.
  const mapped = compiled?.mode === 'mapped';
  markers.forEach((marker) => {
    const word = REFERENCE_WORDS[marker.reason];
    if (!word) return;
    if (!mapped && !SCRIPT_LOCAL_REASONS.has(marker.reason)) return;
    const value = String(marker.value ?? '').trim();
    findings.push(finding(
      'R5',
      sceneForLine(ast, marker.line),
      marker.line,
      `Basil can't find the ${word} “${value}”.`,
      word === 'scene'
        ? `Point this at a scene the script defines, or write a scene called “${value}”.`
        : `Use a ${word} the book already has, or ask the owner to add “${value}”.`,
    ));
  });

  // R7 also speaks for the diagnostics that have no "?" of their own. Reserved syntax the compile
  // could not read is dropped rather than shown to the reader, and a repeated scene name leaves
  // "→ NAME" with two destinations — in both cases the writer would otherwise never learn what
  // happened to the line, so each one is named here with the text it was written with.
  markers.forEach((marker) => {
    const value = String(marker.value ?? '').trim();
    if (marker.reason === 'malformed directive') {
      findings.push(finding(
        'R7',
        sceneForLine(ast, marker.line),
        marker.line,
        `Basil couldn't read the directive “${value}”, so line ${marker.line} isn't in the story.`,
        'Complete the directive, or drop the “>” and the line becomes ordinary writing.',
      ));
    }
    if (marker.reason === 'empty choice answer') {
      findings.push(finding(
        'R7',
        sceneForLine(ast, marker.line),
        marker.line,
        `“${value}” is a choice with no words on it, so line ${marker.line} isn't an answer.`,
        'Write the words the reader taps, or delete the line.',
      ));
    }
    if (marker.reason === 'unknown directive') {
      const text = String(marker.text ?? '').trim();
      findings.push(finding(
        'R7',
        sceneForLine(ast, marker.line),
        marker.line,
        `Basil doesn't know the directive “${value}”, so line ${marker.line} isn't in the story`
          + `${text ? `, and “${text}” never reaches the reader` : ''}.`,
        'Use one of these: remember, forget, if, else, go to, hub, end. Or drop the “>” and the line becomes ordinary writing.',
      ));
    }
    if (marker.reason === 'duplicate scene heading') {
      findings.push(finding(
        'R7',
        sceneForLine(ast, marker.line),
        marker.line,
        `Line ${marker.line} names the scene “${value}”, and line ${marker.firstLine} already names a scene “${value}”.`,
        `Rename the scene on line ${marker.line} or the one on line ${marker.firstLine}, so “${value}” has one meaning.`,
      ));
    }
  });

  // R7 — the compiler's own diagnostic, said on the line the writer sees the "?" on, so the
  // mechanical fix (delete the resolved marker line) still fits it.
  lines.forEach((line, index) => {
    if (!/^\s*\?(?:\s|$)/.test(line)) return;
    const named = (markersByLine.get(index + 1) ?? [])
      .filter((marker) => marker.reason !== 'note')
      .map(diagnostic);
    const note = line.trim().replace(/^\?\s*/, '');
    findings.push(finding(
      'R7',
      sceneForLine(ast, index + 1),
      index + 1,
      named.length
        ? `Basil got stuck here: ${named.join('; ')}.`
        : (note || 'Basil left a question on this line.'),
      "Answer Basil's question, then delete that line.",
    ));
  });

  // One reachability walk, and it is the compiler's own: the rules read the report the compile
  // produced rather than recomputing the graph beside it.
  const nodes = compiled?.nodes ?? compiled?.fixture?.branches ?? [];
  const report = compiled?.report ?? structureReport(nodes, markers);
  const deadEnds = new Set((report.deadEnds ?? []).map(String));
  const unreachable = new Set((report.unreachable ?? []).map(String));
  const sceneLines = new Map(ast.scenes.map((scene): [string, number] => [scene.name.toLocaleLowerCase(), scene.line]));
  scenesOf(nodes).forEach((group) => {
    const line = sceneLines.get(group.name.toLocaleLowerCase()) ?? null;
    if (deadEnds.has(String(group.nodes[group.nodes.length - 1].id))) {
      findings.push(finding(
        'R3',
        group.name,
        line,
        `“${group.name}” stops without an ending or a route forward.`,
        'Add an ending or route this scene to another scene.',
      ));
    }
    if (unreachable.has(String(group.nodes[0].id))) {
      findings.push(finding(
        'R4',
        group.name,
        line,
        `There's no way to reach “${group.name}” from the first scene.`,
        'Point an answer, a check, or a “go to” at this scene.',
      ));
    }
  });

  findings.sort((left, right) => (left.line ?? Number.MAX_SAFE_INTEGER) - (right.line ?? Number.MAX_SAFE_INTEGER)
    || left.rule.localeCompare(right.rule));
  return { findings, characters: characterCoach(ast, lines) };
}
